import { useTellescopeInfiniteQuery, useTellescopeQuery } from "@tellescope/tanstack-query";
import { Form, FormFieldOptions, FormResponseValue, Product } from "@tellescope/types-models";
import { useTranslation } from "react-i18next";
import { responsesSatisfyConditions } from "../../utils";
import { Checkbox } from "@tellescope/ui";
import { ProductWithStripePrice } from "./types";
import { formatStripeProductCost } from "@tellescope/utils";
import { useMemo } from "react";

export interface SelectedProduct {
  id: string;
  cost: number;
}

export interface StripeProductSelectionProps {
  productIds: string[];
  enduserId: string;
  formResponses: FormResponseValue[];
  form: Form;
  options: FormFieldOptions;
  selectedProducts: SelectedProduct[];
  onSelectionChange: (selection: SelectedProduct[]) => void;
}

export const StripeProductSelection = ({
  productIds,
  enduserId,
  formResponses,
  form,
  options,
  selectedProducts,
  onSelectionChange,
}: StripeProductSelectionProps) => {
  const { t } = useTranslation("forms");
  const enduser = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: enduserId,
  });
  const selectedProductIds = useMemo(() => selectedProducts.map(({ id }) => id), [selectedProducts]);
  const stripeProductsQuery = useTellescopeInfiniteQuery({
    model: "products",
    method: "getSome",
    request: {},
    select: (data) =>
      data?.pages.reduce<Product[]>((acc, curr) => {
        const matchingProducts = curr.filter((product) => {
          if (productIds.length && !productIds.includes(product.id)) {
            return false;
          }

          const productCondition = options.productConditions?.find((condition) => condition.productId === product.id);

          if (productCondition?.showCondition === undefined || !Object.keys(productCondition.showCondition).length) {
            return true;
          }

          return responsesSatisfyConditions(formResponses, productCondition.showCondition, {
            dateOfBirth: enduser?.data?.dateOfBirth,
            gender: enduser?.data?.gender,
            state: enduser?.data?.state,
            form,
            activeResponses: formResponses,
          });
        });
        acc.push(...matchingProducts);
        return acc;
      }, []),
  });

  const stripeProductPricesQuery = useTellescopeQuery({
    model: "integrations",
    method: "proxy_read",
    request: {
      integration: "Stripe",
      type: "product-prices",
      id: stripeProductsQuery.data?.map(({ id }) => id).join(","),
      query: options.stripeKey,
    },
    enabled: !!stripeProductsQuery.data?.length,
    select: (data) => data.data as { products: ProductWithStripePrice[] },
  });

  const toggleProduct = (productId: string, cost: number) => {
    const isSelected = selectedProductIds.includes(productId);
    if (isSelected) {
      onSelectionChange(selectedProducts.filter(({ id }) => id !== productId));
    } else {
      onSelectionChange([...selectedProducts, { id: productId, cost }]);
    }
  };

  return (
    <div className="space-y-1">
      <p className="text-muted-foreground text-sm">
        {productIds.length > 1 ? t("Select Products") : t("Select Product")}
      </p>
      {stripeProductsQuery.data?.map((product) => {
        const productWithPrice = stripeProductPricesQuery.data?.products.find(({ _id }) => _id === product.id);

        if (!productWithPrice) return null;

        return (
          <div
            key={product.id}
            role="button"
            onClick={() => toggleProduct(product.id, product.cost.amount)}
            className="flex gap-4 items-center border rounded-md p-2 hover:bg-gray-100"
          >
            <Checkbox checked={selectedProductIds.includes(product.id)} />
            <div className="space-y-0.5">
              <p className="font-semibold text-sm">{product.title}</p>
              {!!product.description && <p className="text-muted-foreground text-xs">{product.description}</p>}
              <p className="text-xs">{formatStripeProductCost(productWithPrice)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};
